import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Alert, Collapse } from 'react-bootstrap';
import serviceApi from '../services/serviceApi';
import { LOCATION_TYPE_LABELS } from '../config/serviceConstants';
import { arabicErrorFor } from '../utils/serviceErrorMessages';

const INITIAL_FORM = {
  name: '',
  type: 'residential',
  city: '',
  district: '',
  street: '',
  building: '',
  unitNumber: '',
  bedrooms: '',
  bathrooms: '',
  sqm: '',
  floorNumber: '',
  hasElevator: false,
  furnished: true,
  hasPets: false,
  accessInstructions: '',
};

const CreateLocationModal = ({ show, onHide, clientId, onCreated }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [showExtras, setShowExtras] = useState(false);
  const [addAnother, setAddAnother] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    if (show && nameRef.current) {
      setTimeout(() => nameRef.current.focus(), 150);
    }
  }, [show]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setShowExtras(false);
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);

    if (!form.name.trim() || !form.type) {
      setError('اسم الموقع ونوعه مطلوبان');
      return;
    }

    // Bedrooms is the catalog-join key — pricingTier is derived from it
    // (decisions #11, #28, #36). Must be present before submit.
    if (form.bedrooms === '' || form.bedrooms === null || form.bedrooms === undefined) {
      setError('عدد غرف النوم مطلوب — أدخل 0 لاستوديو');
      return;
    }
    const bedroomsNum = parseInt(form.bedrooms, 10);
    if (Number.isNaN(bedroomsNum) || bedroomsNum < 0) {
      setError('عدد غرف النوم يجب أن يكون رقماً صحيحاً 0 أو أكبر');
      return;
    }

    const payload = {
      clientId,
      name: form.name.trim(),
      type: form.type,
    };

    // Optional address fields
    const address = {};
    if (form.city.trim()) address.city = form.city.trim();
    if (form.district.trim()) address.district = form.district.trim();
    if (form.street.trim()) address.street = form.street.trim();
    if (form.building.trim()) address.building = form.building.trim();
    if (form.unitNumber.trim()) address.unitNumber = form.unitNumber.trim();
    if (Object.keys(address).length > 0) payload.address = address;

    // Size fields — bedrooms is REQUIRED (validated above; 0 = studio is valid)
    const size = { bedrooms: bedroomsNum };
    if (form.bathrooms) size.bathrooms = parseInt(form.bathrooms, 10);
    if (form.sqm) size.sqm = parseInt(form.sqm, 10);
    payload.size = size;

    // Optional other fields
    if (form.floorNumber) payload.floorNumber = parseInt(form.floorNumber, 10);
    if (form.hasElevator) payload.hasElevator = true;
    if (!form.furnished) payload.furnished = false;
    if (form.hasPets) payload.hasPets = true;
    if (form.accessInstructions.trim()) payload.accessInstructions = form.accessInstructions.trim();

    setSaving(true);
    try {
      await serviceApi.createLocation(payload);
      onCreated();

      if (addAnother) {
        handleReset();
        setTimeout(() => nameRef.current?.focus(), 150);
      } else {
        handleReset();
        onHide();
      }
    } catch (err) {
      setError(arabicErrorFor(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700, color: '#471396' }}>
          إضافة موقع جديد
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        {/* Name */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>اسم الموقع *</Form.Label>
          <Form.Control
            ref={nameRef}
            className="touch-input"
            type="text"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="مثال: شقة الحمراء"
          />
        </Form.Group>

        {/* Type + City */}
        <div className="d-flex gap-3 mb-3">
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>النوع *</Form.Label>
            <Form.Select
              className="touch-input"
              value={form.type}
              onChange={(e) => handleChange('type', e.target.value)}
            >
              {Object.entries(LOCATION_TYPE_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>المدينة</Form.Label>
            <Form.Control
              className="touch-input"
              type="text"
              value={form.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="جدة"
            />
          </Form.Group>
        </div>

        {/* District */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>الحي</Form.Label>
          <Form.Control
            className="touch-input"
            type="text"
            value={form.district}
            onChange={(e) => handleChange('district', e.target.value)}
            placeholder="الحمراء"
          />
        </Form.Group>

        {/* Bedrooms — required (catalog-join key, derives pricingTier) */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>عدد غرف النوم *</Form.Label>
          <Form.Control
            className="touch-input"
            type="number"
            min="0"
            value={form.bedrooms}
            onChange={(e) => handleChange('bedrooms', e.target.value)}
            placeholder="مثال: 2"
            dir="ltr"
          />
          <Form.Text style={{ fontSize: '0.78rem', color: '#6c757d' }}>
            أدخل 0 لاستوديو
          </Form.Text>
        </Form.Group>

        {/* Collapsible extras */}
        <button
          type="button"
          onClick={() => setShowExtras(!showExtras)}
          style={{
            background: 'none',
            border: 'none',
            color: '#471396',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            padding: '4px 0',
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <i className={`bi bi-chevron-${showExtras ? 'up' : 'down'}`} />
          {showExtras ? 'إخفاء التفاصيل الإضافية' : 'تفاصيل إضافية'}
        </button>

        <Collapse in={showExtras}>
          <div>
            {/* Street + Building */}
            <div className="d-flex gap-3 mb-3">
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>الشارع</Form.Label>
                <Form.Control
                  className="touch-input"
                  type="text"
                  value={form.street}
                  onChange={(e) => handleChange('street', e.target.value)}
                />
              </Form.Group>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>المبنى</Form.Label>
                <Form.Control
                  className="touch-input"
                  type="text"
                  value={form.building}
                  onChange={(e) => handleChange('building', e.target.value)}
                />
              </Form.Group>
            </div>

            {/* Unit + Floor */}
            <div className="d-flex gap-3 mb-3">
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>رقم الوحدة</Form.Label>
                <Form.Control
                  className="touch-input"
                  type="text"
                  value={form.unitNumber}
                  onChange={(e) => handleChange('unitNumber', e.target.value)}
                />
              </Form.Group>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>الدور</Form.Label>
                <Form.Control
                  className="touch-input"
                  type="number"
                  min="0"
                  value={form.floorNumber}
                  onChange={(e) => handleChange('floorNumber', e.target.value)}
                  dir="ltr"
                />
              </Form.Group>
            </div>

            {/* Bathrooms + sqm (bedrooms moved to top-level — required field) */}
            <div className="d-flex gap-3 mb-3">
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>الحمامات</Form.Label>
                <Form.Control
                  className="touch-input"
                  type="number"
                  min="0"
                  value={form.bathrooms}
                  onChange={(e) => handleChange('bathrooms', e.target.value)}
                  dir="ltr"
                />
              </Form.Group>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>المساحة م²</Form.Label>
                <Form.Control
                  className="touch-input"
                  type="number"
                  min="0"
                  value={form.sqm}
                  onChange={(e) => handleChange('sqm', e.target.value)}
                  dir="ltr"
                />
              </Form.Group>
            </div>

            {/* Checkboxes */}
            <div className="d-flex gap-4 mb-3 flex-wrap">
              <Form.Check
                type="checkbox"
                label="يوجد مصعد"
                checked={form.hasElevator}
                onChange={(e) => handleChange('hasElevator', e.target.checked)}
                style={{ fontSize: '0.88rem' }}
              />
              <Form.Check
                type="checkbox"
                label="مفروشة"
                checked={form.furnished}
                onChange={(e) => handleChange('furnished', e.target.checked)}
                style={{ fontSize: '0.88rem' }}
              />
              <Form.Check
                type="checkbox"
                label="يوجد حيوانات أليفة"
                checked={form.hasPets}
                onChange={(e) => handleChange('hasPets', e.target.checked)}
                style={{ fontSize: '0.88rem' }}
              />
            </div>

            {/* Access instructions */}
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>تعليمات الوصول</Form.Label>
              <Form.Control
                className="touch-input"
                as="textarea"
                rows={2}
                value={form.accessInstructions}
                onChange={(e) => handleChange('accessInstructions', e.target.value)}
                placeholder="طريقة الدخول، رمز البوابة، ملاحظات..."
              />
            </Form.Group>
          </div>
        </Collapse>

        {/* Add another checkbox */}
        <Form.Check
          type="checkbox"
          label="إضافة موقع آخر بعد الحفظ"
          checked={addAnother}
          onChange={(e) => setAddAnother(e.target.checked)}
          style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: 8 }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>إلغاء</Button>
        <Button
          style={{ background: '#471396', borderColor: '#471396' }}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? <span className="spinner-border spinner-border-sm" /> : 'إضافة الموقع'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateLocationModal;
